import React, { useEffect, useRef, useState, useCallback } from 'react'
import * as THREE from 'three'
import { SoundToggleBtn } from '../ui/SoundToggleBtn'
import { fmtCoins } from '../../lib/cards'
import { io, Socket } from 'socket.io-client'
import { Sounds } from '../../lib/sounds'

interface Props {
  token:string; balance:number|null; playerId?:string; operatorId?:string
}
type Phase = 'waiting'|'flying'|'crashed'

// ─── Spaceship builder ────────────────────────────────────────────────────────
function buildSpaceship(scene: THREE.Scene): THREE.Group {
  const ship = new THREE.Group()

  const metal  = new THREE.MeshStandardMaterial({color:0xd0e8ff,roughness:.2,metalness:.6,emissive:new THREE.Color(0x223344),emissiveIntensity:.2})
  const dark   = new THREE.MeshStandardMaterial({color:0x223344,roughness:.5,metalness:.6})
  const accent = new THREE.MeshStandardMaterial({color:0x66aaff,roughness:.1,metalness:.8,emissive:new THREE.Color(0x2244cc),emissiveIntensity:1.5})
  const glass  = new THREE.MeshStandardMaterial({color:0x88ccff,roughness:.05,metalness:.1,
    transparent:true,opacity:.7,emissive:new THREE.Color(0x224488),emissiveIntensity:.4})
  const hot    = new THREE.MeshStandardMaterial({color:0xff6600,roughness:.1,metalness:.0,
    emissive:new THREE.Color(0xff3300),emissiveIntensity:2})

  // ── Fuselage (main body, nose-up) ─────────────────────────────────────────
  const body = new THREE.Mesh(new THREE.CylinderGeometry(.28,.38,2.6,16), metal)
  ship.add(body)

  // Nose cone
  const nose = new THREE.Mesh(new THREE.ConeGeometry(.28,.9,16), metal)
  nose.position.y = 1.75; ship.add(nose)

  // Cockpit dome
  const dome = new THREE.Mesh(new THREE.SphereGeometry(.22,.16,16,0,Math.PI*2,0,Math.PI*.6), glass)
  dome.position.y = 1.2; ship.add(dome)

  // ── Wings (4 swept delta wings) ───────────────────────────────────────────
  const wingShape = new THREE.Shape()
  wingShape.moveTo(0, 0)
  wingShape.lineTo(1.6,-0.3)
  wingShape.lineTo(1.4,-1.0)
  wingShape.lineTo(0.15,-1.2)
  wingShape.lineTo(0,-0.5)
  wingShape.closePath()
  const wingGeo = new THREE.ExtrudeGeometry(wingShape,{depth:.06,bevelEnabled:false})
  for(let i=0;i<4;i++){
    const wing = new THREE.Mesh(wingGeo, i%2===0?metal:dark)
    wing.rotation.y = (i*Math.PI/2)
    wing.position.y = -0.6
    // Align wing to sweep backward
    wing.rotation.x = -0.12
    ship.add(wing)
  }

  // ── Engine pods (3 around base) ───────────────────────────────────────────
  for(let i=0;i<3;i++){
    const ang = (i/3)*Math.PI*2
    const pod = new THREE.Mesh(new THREE.CylinderGeometry(.1,.14,.7,10), dark)
    pod.position.set(Math.cos(ang)*.3, -1.45, Math.sin(ang)*.3)
    ship.add(pod)
    // Engine nozzle
    const nozzle = new THREE.Mesh(new THREE.CylinderGeometry(.14,.1,.2,10), accent)
    nozzle.position.set(Math.cos(ang)*.3, -1.85, Math.sin(ang)*.3)
    ship.add(nozzle)
    // Engine glow disc
    const glow = new THREE.Mesh(new THREE.CircleGeometry(.1,10), hot)
    glow.position.set(Math.cos(ang)*.3,-1.96,Math.sin(ang)*.3)
    glow.rotation.x=Math.PI/2; ship.add(glow)
  }

  // ── Accent stripes ────────────────────────────────────────────────────────
  const stripe = new THREE.Mesh(
    new THREE.CylinderGeometry(.285,.285,.15,16), accent)
  stripe.position.y=0.4; ship.add(stripe)
  const stripe2 = new THREE.Mesh(
    new THREE.CylinderGeometry(.285,.285,.08,16), accent)
  stripe2.position.y=-0.3; ship.add(stripe2)

  // ── Engine light ──────────────────────────────────────────────────────────
  const engineLight = new THREE.PointLight(0xff6600,8,6)
  engineLight.position.y=-2; ship.add(engineLight)

  ship.position.set(0,0,0)
  // Self-illumination light
  const selfLight = new THREE.PointLight(0xaaddff,4,8)
  selfLight.position.set(0,1,2)
  ship.add(selfLight)
  ship.castShadow=true
  scene.add(ship)
  return ship
}

// ─── Particle system ──────────────────────────────────────────────────────────
const MAX_PARTS = 200
interface Particle { x:number;y:number;z:number; vx:number;vy:number;vz:number; life:number; maxLife:number }

function createParticleSystem(scene:THREE.Scene) {
  const positions = new Float32Array(MAX_PARTS*3)
  const colors    = new Float32Array(MAX_PARTS*3)
  const sizes     = new Float32Array(MAX_PARTS)
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position',new THREE.BufferAttribute(positions,3))
  geo.setAttribute('color',   new THREE.BufferAttribute(colors,3))
  geo.setAttribute('size',    new THREE.BufferAttribute(sizes,1))
  const mat = new THREE.PointsMaterial({
    size:.45, vertexColors:true, transparent:true,
    opacity:1.0, sizeAttenuation:true, blending:THREE.AdditiveBlending,
    depthWrite:false,
  })
  const points = new THREE.Points(geo,mat)
  scene.add(points)
  return { points, geo, positions, colors, sizes, particles:[] as Particle[] }
}

function spawnParticle(ps:ReturnType<typeof createParticleSystem>, shipPos:THREE.Vector3) {
  if(ps.particles.length>=MAX_PARTS) ps.particles.shift()
  const spread=0.15
  const spread2=0.22
  ps.particles.push({
    x:shipPos.x+(Math.random()-.5)*spread2,
    y:shipPos.y-1.85+(Math.random()-.5)*.1,
    z:shipPos.z+(Math.random()-.5)*spread2,
    vx:(Math.random()-.5)*.06,
    vy:-0.12-Math.random()*.15,
    vz:(Math.random()-.5)*.06,
    life:0, maxLife:30+Math.random()*25,
  })
}

function updateParticles(ps:ReturnType<typeof createParticleSystem>) {
  ps.particles.forEach(p=>{
    p.x+=p.vx; p.y+=p.vy; p.z+=p.vz
    p.vx*=0.97; p.vz*=0.97
    p.life++
  })
  ps.particles=ps.particles.filter(p=>p.life<p.maxLife)

  for(let i=0;i<MAX_PARTS;i++){
    const p=ps.particles[i]
    if(p){
      ps.positions[i*3]=p.x; ps.positions[i*3+1]=p.y; ps.positions[i*3+2]=p.z
      const t=1-p.life/p.maxLife
      // Colour: white hot → orange → red → transparent
      // White core → orange → red → smoke
      ps.colors[i*3]  = t>0.6?1.0:t>0.3?1.0:0.7
      ps.colors[i*3+1]= t>0.6?1.0:t>0.3?0.5:0.15
      ps.colors[i*3+2]= t>0.6?0.8:t>0.3?0.1:0.05
      ps.sizes[i]=t*0.6+0.12
    } else {
      ps.positions[i*3]=ps.positions[i*3+1]=ps.positions[i*3+2]=9999
      ps.sizes[i]=0
    }
  }
  ps.geo.attributes.position.needsUpdate=true
  ps.geo.attributes.color.needsUpdate=true
  ps.geo.attributes.size.needsUpdate=true
}

// ─── Starfield ────────────────────────────────────────────────────────────────
function createStars(scene:THREE.Scene){
  const N=2000, pos=new Float32Array(N*3)
  for(let i=0;i<N;i++){
    pos[i*3]  =(Math.random()-.5)*200
    pos[i*3+1]=(Math.random()-.5)*200
    pos[i*3+2]=(Math.random()-.5)*200
  }
  const geo=new THREE.BufferGeometry()
  geo.setAttribute('position',new THREE.BufferAttribute(pos,3))
  const mat=new THREE.PointsMaterial({color:0xffffff,size:.35,sizeAttenuation:true})
  scene.add(new THREE.Points(geo,mat))
}

// ─── Launch pad ───────────────────────────────────────────────────────────────
function createPad(scene:THREE.Scene){
  const matGrey=new THREE.MeshStandardMaterial({color:0x445566,roughness:.8,metalness:.3})
  const matGold=new THREE.MeshStandardMaterial({color:0xc8a020,roughness:.3,metalness:.8})
  // Main platform
  const pad=new THREE.Mesh(new THREE.CylinderGeometry(2.5,2.8,.3,32),matGrey)
  pad.position.y=-2.3; scene.add(pad)
  // Rim
  const rim=new THREE.Mesh(new THREE.TorusGeometry(2.6,.12,8,32),matGold)
  rim.rotation.x=Math.PI/2; rim.position.y=-2.1; scene.add(rim)
  // Support legs
  for(let i=0;i<4;i++){
    const a=(i/4)*Math.PI*2
    const leg=new THREE.Mesh(new THREE.CylinderGeometry(.1,.15,1.5,8),matGrey)
    leg.position.set(Math.cos(a)*2.2,-2.8,Math.sin(a)*2.2)
    leg.rotation.z=Math.cos(a)*.2; leg.rotation.x=Math.sin(a)*.2; scene.add(leg)
  }
  // Launch flame ring
  const ring=new THREE.Mesh(
    new THREE.TorusGeometry(1,.2,8,32),
    new THREE.MeshStandardMaterial({color:0xff4400,emissive:new THREE.Color(0xff2200),
      emissiveIntensity:1,roughness:.1}))
  ring.rotation.x=Math.PI/2; ring.position.y=-2.05; scene.add(ring)
}

// ─── Main Component ───────────────────────────────────────────────────────────
export function Crash3DGame({token,balance:initBalance,playerId:initPid,operatorId:initOid}:Props){
  const mountRef  = useRef<HTMLDivElement>(null)
  const socketRef = useRef<Socket|null>(null)
  const sessionRef= useRef({playerId:initPid||'',operatorId:initOid||''})

  const rendRef   = useRef<THREE.WebGLRenderer|null>(null)
  const sceneRef  = useRef<THREE.Scene|null>(null)
  const camRef    = useRef<THREE.PerspectiveCamera|null>(null)
  const shipRef   = useRef<THREE.Group|null>(null)
  const psRef     = useRef<ReturnType<typeof createParticleSystem>|null>(null)
  const rafRef    = useRef(0)

  const flightRef = useRef({
    active:    false,
    crashed:   false,
    elapsed:   0,
    mult:      1.0,
    camTarget: new THREE.Vector3(0,4,12),
    shake:     0,
  })

  const [phase,setPhase]         = useState<Phase>('waiting')
  const [balance,setBalance]     = useState<number|null>(initBalance)
  const [multiplier,setMultiplier] = useState(1.0)
  const [countdown,setCountdown] = useState(10)
  const [stake,setStake]         = useState(20)
  const [myBet,setMyBet]         = useState<number|null>(null)
  const [cashedOut,setCashedOut] = useState(false)
  const [cashedAt,setCashedAt]   = useState<number|null>(null)
  const [crashAt,setCrashAt]     = useState<number|null>(null)
  const [history,setHistory]     = useState<number[]>([])
  const [toast,setToast]         = useState('')
  const [currency,setCurrency]   = useState('coins')

  useEffect(()=>{ if(initBalance!==null) setBalance(initBalance) },[initBalance])
  const showToast=(msg:string,d=2500)=>{ setToast(msg); setTimeout(()=>setToast(''),d) }

  // ── Three.js setup ──────────────────────────────────────────────────────────
  useEffect(()=>{
    const el=mountRef.current; if(!el) return
    const W=el.clientWidth, H=el.clientHeight

    const renderer=new THREE.WebGLRenderer({antialias:true})
    renderer.setSize(W,H); renderer.setPixelRatio(Math.min(window.devicePixelRatio,2))
    renderer.shadowMap.enabled=true; renderer.setClearColor(0x010208)
    el.appendChild(renderer.domElement); rendRef.current=renderer

    const scene=new THREE.Scene()
    scene.fog=new THREE.FogExp2(0x010208,.018); sceneRef.current=scene

    const cam=new THREE.PerspectiveCamera(55,W/H,.1,500)
    cam.position.set(0,4,12); cam.lookAt(0,2,0); camRef.current=cam

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff,2.5))
    const sun=new THREE.DirectionalLight(0xffffff,3.0)
    sun.position.set(5,10,8); sun.castShadow=true; scene.add(sun)
    const fill=new THREE.DirectionalLight(0xaaccff,2.0)
    fill.position.set(-8,6,-4); scene.add(fill)
    const front=new THREE.DirectionalLight(0xffeedd,2.0)
    front.position.set(0,3,15); scene.add(front)
    const under=new THREE.DirectionalLight(0x8899ff,1.0)
    under.position.set(0,-5,5); scene.add(under)

    createStars(scene)
    createPad(scene)

    const ship=buildSpaceship(scene); shipRef.current=ship
    const ps=createParticleSystem(scene); psRef.current=ps

    const onResize=()=>{
      const w=el.clientWidth,h=el.clientHeight
      renderer.setSize(w,h); cam.aspect=w/h; cam.updateProjectionMatrix()
    }
    window.addEventListener('resize',onResize)

    const camVel=new THREE.Vector3()
    let frame=0
    const loop=()=>{
      rafRef.current=requestAnimationFrame(loop)
      const f=flightRef.current; frame++

      if(f.active&&!f.crashed&&ship){
        f.elapsed+=16
        // Fly upward with slight wobble
        const spd=0.015+f.mult*.008
        ship.position.y+=spd
        ship.position.x+=Math.sin(f.elapsed*.0008)*.01
        ship.rotation.z=Math.sin(f.elapsed*.0005)*.04
        ship.rotation.x=-0.15  // slight nose-up tilt

        // Spawn engine particles
        spawnParticle(ps, ship.position)
        if(frame%2===0) spawnParticle(ps, ship.position)
        updateParticles(ps)

        // Camera follows with lag
        const tgt=new THREE.Vector3(
          ship.position.x*0.3,
          ship.position.y-2+Math.min(f.mult*0.5,8),
          8+Math.min(f.mult*.8,18)
        )
        cam.position.lerp(tgt,.04)
        cam.lookAt(ship.position.x*.5, ship.position.y+1, 0)

      } else if(f.crashed){
        // Camera shake
        if(f.shake>0){
          cam.position.x+=(Math.random()-.5)*f.shake*.1
          cam.position.y+=(Math.random()-.5)*f.shake*.1
          f.shake*=.88
        }
        updateParticles(ps)

        // Slow cam return
        const home=new THREE.Vector3(0,4,12)
        cam.position.lerp(home,.015)
        cam.lookAt(0,2,0)

      } else {
        // Idle: gentle ship bob on pad
        if(ship){
          ship.position.y=Math.sin(Date.now()*.001)*.08
          ship.rotation.y+=.003
        }
        updateParticles(ps)
        cam.position.lerp(new THREE.Vector3(0,4,12),.03)
        cam.lookAt(0,2,0)
      }

      renderer.render(scene,cam)
    }
    rafRef.current=requestAnimationFrame(loop)

    return()=>{
      window.removeEventListener('resize',onResize)
      cancelAnimationFrame(rafRef.current)
      renderer.dispose(); el.removeChild(renderer.domElement)
    }
  },[])

  // ── Explosion ────────────────────────────────────────────────────────────
  const triggerExplosion=useCallback(()=>{
    const ps=psRef.current; const ship=shipRef.current; if(!ps||!ship) return
    flightRef.current.crashed=true; flightRef.current.active=false
    flightRef.current.shake=1.5

    // Burst 80 particles outward
    for(let i=0;i<80;i++){
      const a=Math.random()*Math.PI*2, b=(Math.random()-.5)*Math.PI
      const spd=.08+Math.random()*.2
      if(ps.particles.length<MAX_PARTS){
        ps.particles.push({
          x:ship.position.x,y:ship.position.y,z:ship.position.z,
          vx:Math.cos(a)*Math.cos(b)*spd,
          vy:Math.sin(b)*spd,
          vz:Math.sin(a)*Math.cos(b)*spd,
          life:0,maxLife:60+Math.random()*40,
        })
      }
    }
    // Hide ship
    ship.visible=false
    // Show ship again after reset
    setTimeout(()=>{ ship.visible=true; ship.position.set(0,0,0); ship.rotation.set(0,0,0) },4000)
  },[])

  // ── Socket ────────────────────────────────────────────────────────────────
  useEffect(()=>{
    const GS=(import.meta as any).env?.VITE_GAME_SERVER_URL||'http://localhost:3002'
    const s=io(GS,{transports:['polling'],auth:{token}})
    socketRef.current=s
    try{
      const p=JSON.parse(atob(token.split('.')[1]))
      sessionRef.current={playerId:p.sub||'',operatorId:p.operator_id||''}
      if(p.currency) setCurrency(p.currency)
    }catch{}

    s.on('connect',async()=>{
      try{
        const {playerId}=sessionRef.current
        const r=await fetch(`${GS}/wallet/balance?player_id=${playerId}`)
        const d=await r.json(); if(typeof d.balance==='number') setBalance(d.balance)
      }catch{}
    })

    s.on('c3d:waiting',(d:any)=>{
      setPhase('waiting'); setCountdown(Math.ceil(d.countdown_ms/1000))
      setMultiplier(1.0); setMyBet(null); setCashedOut(false)
      setCashedAt(null); setCrashAt(null)
      if(d.history) setHistory(d.history)
      flightRef.current.active=false; flightRef.current.crashed=false
      flightRef.current.elapsed=0; flightRef.current.mult=1.0
      Sounds.aviatorBetting?.()
    })

    s.on('c3d:countdown',(d:any)=>{
      setCountdown(Math.ceil(d.countdown_ms/1000))
    })

    s.on('c3d:start',()=>{
      setPhase('flying'); flightRef.current.active=true; flightRef.current.crashed=false
      flightRef.current.elapsed=0; flightRef.current.mult=1.0
      Sounds.aviatorTakeoff?.()
    })

    s.on('c3d:tick',(d:any)=>{
      const m=d.multiplier||1
      setMultiplier(m); flightRef.current.mult=m
      Sounds.aviatorTick?.(m)
    })

    s.on('c3d:crash',(d:any)=>{
      const ca=d.crash_at||1
      setCrashAt(ca); setPhase('crashed')
      flightRef.current.active=false
      triggerExplosion()
      setHistory(h=>[ca,...h].slice(0,10))
      Sounds.aviatorCrash?.()
    })

    s.on('c3d:bet_placed',(d:any)=>{
      setMyBet(d.stake)
      showToast(`🚀 Bet ₹${d.stake} placed!`,1800)
    })

    s.on('c3d:cashout_result',(d:any)=>{
      setCashedOut(true); setCashedAt(d.cash_at)
      if(typeof d.balance==='number') setBalance(d.balance)
      showToast(`💰 Cashed out at ${d.cash_at.toFixed(2)}× — ₹${d.payout.toFixed(2)}!`,3000)
      Sounds.aviatorCashout?.()
    })

    s.on('c3d:result',(d:any)=>{
      if(typeof d.balance==='number') setBalance(d.balance)
    })

    s.on('c3d:error',(d:any)=>{ showToast(`❌ ${d.error}`) })
    s.on('balance',(d:any)=>{ if(typeof d.balance==='number') setBalance(d.balance) })
    return()=>{ s.disconnect() }
  },[token,triggerExplosion])

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleBet=()=>{
    const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
    if(!s||phase!=='waiting'||myBet!==null) return
    if(balance!==null&&balance<stake) return showToast('Not enough balance')
    setBalance(b=>b!==null?Math.max(0,b-stake):b)
    s.emit('c3d:bet',{player_id:playerId,operator_id:operatorId,stake})
    Sounds.go()
  }

  const handleCashout=()=>{
    const s=socketRef.current; const {playerId,operatorId}=sessionRef.current
    if(!s||phase!=='flying'||!myBet||cashedOut) return
    s.emit('c3d:cashout',{player_id:playerId,operator_id:operatorId})
  }

  const STAKES=[5,10,20,50,100,200,500]
  const multColor = multiplier<2?'#60a5fa':multiplier<5?'#4ade80':multiplier<10?'#fbbf24':'#f87171'

  return(
    <div style={{display:'flex',flexDirection:'column',height:'100%',
      background:'#010208',color:'#fff',fontFamily:'system-ui,sans-serif',
      userSelect:'none',overflow:'hidden',position:'relative'}}>
      <style>{`
        @keyframes c3Toast{0%{opacity:0;transform:translateX(-50%) scale(.85)}
          15%,85%{opacity:1;transform:translateX(-50%) scale(1)}100%{opacity:0}}
        @keyframes c3Pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}
        @keyframes c3Crash{0%{transform:translateX(-50%) scale(1)}
          25%{transform:translateX(-48%) scale(1.1)}
          75%{transform:translateX(-52%) scale(1.1)}
          100%{transform:translateX(-50%) scale(1)}}
        .c3btn:hover{filter:brightness(1.15)}.c3btn:active{transform:scale(.96)!important}
      `}</style>

      {/* Header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'6px 12px',background:'rgba(0,0,0,.7)',
        borderBottom:'1px solid rgba(100,150,255,.15)',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:16}}>🚀</span>
          <strong style={{fontSize:12,letterSpacing:.8,color:'#88aaff'}}>CRASH</strong>
          <span style={{fontSize:9,color:'rgba(255,255,255,.3)'}}>3D • To the Moon</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <SoundToggleBtn/>
          <div style={{background:'rgba(100,150,255,.1)',border:'1px solid rgba(100,150,255,.3)',
            borderRadius:7,padding:'3px 9px',fontWeight:700,color:'#88aaff',fontSize:12}}>
            {balance!==null?`₹${fmtCoins(balance)}`:'—'}
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast&&(
        <div style={{position:'absolute',top:52,left:'50%',zIndex:60,
          animation:'c3Toast 2.5s ease-in-out forwards',
          background:'rgba(0,0,0,.92)',color:'#88aaff',padding:'7px 18px',
          borderRadius:20,fontSize:14,fontWeight:800,whiteSpace:'nowrap',
          border:'1px solid rgba(100,150,255,.4)'}}>
          {toast}
        </div>
      )}

      {/* 3D Canvas */}
      <div ref={mountRef} style={{flex:1,minHeight:0,position:'relative'}}>

        {/* Multiplier display */}
        <div style={{position:'absolute',top:'50%',left:'50%',
          transform:'translate(-50%,-50%)',zIndex:10,textAlign:'center',
          pointerEvents:'none',
          animation:phase==='crashed'?'c3Crash .3s ease-in-out':
            phase==='flying'?'c3Pulse 1s infinite':'none',
        }}>
          {phase==='flying'&&(
            <div style={{fontSize:52,fontWeight:900,color:multColor,
              textShadow:`0 0 20px ${multColor},0 0 40px ${multColor}`,
              fontVariantNumeric:'tabular-nums',lineHeight:1}}>
              {multiplier.toFixed(2)}×
            </div>
          )}
          {phase==='crashed'&&crashAt&&(
            <div>
              <div style={{fontSize:18,fontWeight:900,color:'#ef4444',
                textShadow:'0 0 20px #ef4444',marginBottom:4}}>
                💥 CRASHED
              </div>
              <div style={{fontSize:36,fontWeight:900,color:'#ef4444',
                textShadow:'0 0 30px #ef4444'}}>
                {crashAt.toFixed(2)}×
              </div>
            </div>
          )}
          {phase==='waiting'&&(
            <div style={{fontSize:14,fontWeight:700,color:'rgba(255,255,255,.5)',
              background:'rgba(0,0,0,.6)',borderRadius:12,padding:'6px 16px',
              border:'1px solid rgba(100,150,255,.2)'}}>
              🚀 Launching in {countdown}s
            </div>
          )}
        </div>

        {/* My bet status */}
        {myBet&&phase==='flying'&&!cashedOut&&(
          <div style={{position:'absolute',top:12,right:12,zIndex:10,
            background:'rgba(0,0,0,.7)',border:'1px solid rgba(100,150,255,.3)',
            borderRadius:10,padding:'5px 12px',fontSize:11,color:'#88aaff',textAlign:'right'}}>
            <div style={{fontWeight:700}}>₹{myBet}</div>
            <div style={{color:'#4ade80',fontSize:10}}>
              → ₹{(myBet*multiplier).toFixed(2)}
            </div>
          </div>
        )}
        {cashedOut&&cashedAt&&(
          <div style={{position:'absolute',top:12,right:12,zIndex:10,
            background:'rgba(74,222,128,.15)',border:'1px solid rgba(74,222,128,.4)',
            borderRadius:10,padding:'5px 12px',fontSize:11,color:'#4ade80',textAlign:'right'}}>
            <div style={{fontWeight:800}}>✅ {cashedAt.toFixed(2)}×</div>
            <div>₹{(myBet!*cashedAt).toFixed(2)}</div>
          </div>
        )}

        {/* History */}
        <div style={{position:'absolute',bottom:6,left:'50%',transform:'translateX(-50%)',
          display:'flex',gap:4,zIndex:10}}>
          {history.slice(0,8).map((h,i)=>(
            <div key={i} style={{
              borderRadius:8,padding:'1px 6px',fontSize:9,fontWeight:800,
              background:h<2?'rgba(239,68,68,.2)':h<5?'rgba(74,222,128,.2)':'rgba(251,191,36,.2)',
              color:h<2?'#f87171':h<5?'#4ade80':'#fbbf24',
              border:`1px solid ${h<2?'rgba(239,68,68,.3)':h<5?'rgba(74,222,128,.3)':'rgba(251,191,36,.3)'}`,
              opacity:1-i*.1,
            }}>{h.toFixed(2)}×</div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div style={{background:'linear-gradient(to top,rgba(0,0,0,.98),rgba(0,0,0,.75))',
        borderTop:'1px solid rgba(100,150,255,.1)',
        padding:'7px 10px 11px',flexShrink:0}}>

        {/* Stake */}
        <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:7}}>
          <span style={{fontSize:9,color:'rgba(255,255,255,.3)',whiteSpace:'nowrap'}}>BET:</span>
          <div style={{display:'flex',gap:3,overflowX:'auto',flex:1}}>
            {STAKES.map(sv=>(
              <button key={sv} className="c3btn"
                onClick={()=>phase==='waiting'&&!myBet&&setStake(sv)}
                style={{
                  borderRadius:5,padding:'3px 7px',cursor:'pointer',flexShrink:0,
                  border:stake===sv?'1.5px solid #88aaff':'1.5px solid rgba(255,255,255,.1)',
                  background:stake===sv?'rgba(100,150,255,.18)':'rgba(255,255,255,.04)',
                  color:stake===sv?'#88aaff':'rgba(255,255,255,.4)',
                  fontSize:10,fontWeight:700,whiteSpace:'nowrap',transition:'all .1s',
                }}>
                ₹{sv>=1000?`${sv/1000}K`:sv}
              </button>
            ))}
          </div>
        </div>

        {/* Action button */}
        {phase==='waiting'&&!myBet&&(
          <button className="c3btn" onClick={handleBet}
            style={{width:'100%',padding:'11px 0',borderRadius:10,border:'none',
              cursor:'pointer',fontWeight:900,fontSize:15,transition:'all .15s',
              background:'linear-gradient(135deg,#3b6bdf,#1a3a8f)',color:'#fff',
              boxShadow:'0 4px 20px rgba(59,107,223,.4)'}}>
            🚀 BET ₹{stake}
          </button>
        )}
        {phase==='waiting'&&myBet&&(
          <div style={{width:'100%',padding:'11px 0',borderRadius:10,textAlign:'center',
            background:'rgba(74,222,128,.1)',border:'1px solid rgba(74,222,128,.3)',
            color:'#4ade80',fontWeight:700,fontSize:13}}>
            ✅ Bet ₹{myBet} placed — waiting for launch...
          </div>
        )}
        {phase==='flying'&&myBet&&!cashedOut&&(
          <button className="c3btn" onClick={handleCashout}
            style={{width:'100%',padding:'11px 0',borderRadius:10,border:'none',
              cursor:'pointer',fontWeight:900,fontSize:15,transition:'all .15s',
              background:'linear-gradient(135deg,#16a34a,#15803d)',color:'#fff',
              boxShadow:'0 4px 20px rgba(22,163,74,.4)',
              animation:'c3Pulse .8s infinite'}}>
            💰 CASH OUT {multiplier.toFixed(2)}× — ₹{(myBet*multiplier).toFixed(2)}
          </button>
        )}
        {phase==='flying'&&!myBet&&(
          <div style={{width:'100%',padding:'11px 0',borderRadius:10,textAlign:'center',
            background:'rgba(255,255,255,.04)',color:'rgba(255,255,255,.25)',fontSize:13}}>
            Bet before next round
          </div>
        )}
        {phase==='flying'&&myBet&&cashedOut&&(
          <div style={{width:'100%',padding:'11px 0',borderRadius:10,textAlign:'center',
            background:'rgba(74,222,128,.1)',border:'1px solid rgba(74,222,128,.3)',
            color:'#4ade80',fontWeight:700,fontSize:13}}>
            ✅ Cashed out at {cashedAt?.toFixed(2)}× — next round coming
          </div>
        )}
        {phase==='crashed'&&(
          <div style={{width:'100%',padding:'11px 0',borderRadius:10,textAlign:'center',
            background:'rgba(239,68,68,.08)',border:'1px solid rgba(239,68,68,.2)',
            color:'rgba(255,255,255,.4)',fontSize:12}}>
            💥 Crashed at {crashAt?.toFixed(2)}× — new round in a moment...
          </div>
        )}
      </div>
    </div>
  )
}
